import { ReactNode, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import LockResetIcon from '@mui/icons-material/LockReset';
import { Logo } from '../Logo';
import { useAuth } from '../../hooks/useAuth';

export interface NavItem {
  label?: string;
  to?: string;
  icon?: ReactNode;
  /** pokud true, active zalozka se testuje pouze na presnou shodu (pro Dashboard atp.) */
  exact?: boolean;
  /** Odddeluje sekce v sidebaru (nezobrazuje se jako link) */
  divider?: boolean;
  /** Nadpis sekce */
  sectionLabel?: string;
  /** Misto navigace otevre callback (napr. dialog). Ma prednost pred `to`. */
  onClick?: () => void;
}

interface AppShellProps {
  navItems: NavItem[];
  title: string;
  children: ReactNode;
}

const DRAWER_WIDTH = 256;

export const AppShell = ({ navItems, title, children }: AppShellProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [userMenu, setUserMenu] = useState<null | HTMLElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const isActiveItem = (item: NavItem): boolean => {
    if (item.divider || !item.to) return false;
    const itemPath = item.to.split('?')[0];
    if (item.exact) return location.pathname === itemPath;
    return location.pathname === itemPath || location.pathname.startsWith(itemPath + '/');
  };

  const itemSx = {
    minHeight: 44,
    px: 2.5,
    // Zadna barevna cara vlevo — selected state je jen jemny background tint + tucnejsi text.
    '&.Mui-selected': {
      bgcolor: alpha(theme.palette.primary.main, 0.08),
      color: 'primary.main',
      '& .MuiListItemIcon-root': { color: 'primary.main' },
      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.14) },
    },
  } as const;

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar placeholder pod fixed AppBar */}
      <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }} />
      <Divider />
      <List sx={{ flex: 1, overflowY: 'auto', py: 0 }}>
        {navItems.map((item, idx) => {
          if (item.divider) {
            return item.sectionLabel ? (
              <ListSubheader
                key={`sub-${idx}`}
                disableSticky
                sx={{
                  fontSize: 11,
                  lineHeight: '32px',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  bgcolor: 'transparent',
                  mt: 1,
                  fontWeight: 700,
                }}
              >
                {item.sectionLabel}
              </ListSubheader>
            ) : (
              <Divider key={`div-${idx}`} sx={{ my: 0.5 }} />
            );
          }
          const selected = isActiveItem(item);
          const key = item.to ?? `cb-${idx}-${item.label}`;
          const content = (
            <>
              <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: '0.875rem',
                  fontWeight: selected ? 600 : 400,
                }}
              />
            </>
          );
          if (item.onClick) {
            return (
              <ListItem key={key} disablePadding>
                <ListItemButton
                  onClick={() => {
                    if (isMobile) setDrawerOpen(false);
                    item.onClick!();
                  }}
                  sx={itemSx}
                >
                  {content}
                </ListItemButton>
              </ListItem>
            );
          }
          return (
            <ListItem key={key} disablePadding>
              <ListItemButton
                component={RouterLink}
                to={item.to!}
                selected={selected}
                onClick={() => isMobile && setDrawerOpen(false)}
                sx={itemSx}
              >
                {content}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          © {new Date().getFullYear()} HasičiOnline.cz
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            onClick={() => setDrawerOpen(!drawerOpen)}
            sx={{ mr: 1 }}
            aria-label="Otevřít menu"
          >
            <MenuIcon />
          </IconButton>
          <Logo size={32} />
          <Box sx={{ flexGrow: 1 }} />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: { xs: 'none', md: 'block' },
              mr: 2,
              textTransform: 'uppercase',
              fontWeight: 700,
              letterSpacing: 0.5,
            }}
          >
            {title}
          </Typography>
          <Tooltip title={`${user?.displayName ?? user?.username} • ${user?.role}`}>
            <IconButton onClick={(e) => setUserMenu(e.currentTarget)} sx={{ p: 0 }}>
              <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: '0.95rem' }}>
                {(user?.displayName ?? user?.username ?? '?').charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={userMenu}
            open={!!userMenu}
            onClose={() => setUserMenu(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { minWidth: 240, mt: 0.5 } }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="body2" fontWeight={600}>
                {user?.displayName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.username} • {user?.role}
                {user?.unitName && ` • ${user.unitName}`}
              </Typography>
            </Box>
            <Divider />
            <MenuItem
              onClick={() => {
                setUserMenu(null);
                const base =
                  user?.role === 'SUPER_ADMIN'
                    ? '/superadmin'
                    : user?.role === 'UNIT_ADMIN'
                      ? '/admin'
                      : '/recorder';
                navigate(`${base}/change-password`);
              }}
            >
              <ListItemIcon>
                <LockResetIcon fontSize="small" />
              </ListItemIcon>
              Změnit heslo
            </MenuItem>
            <MenuItem
              onClick={() => {
                setUserMenu(null);
                logout();
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Odhlásit
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Desktop: permanent drawer (vzdy viditelny); Mobile: temporary overlay */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            [`& .MuiDrawer-paper`]: {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
            },
          }}
        >
          {drawer}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              borderRight: 1,
              borderColor: 'divider',
            },
          }}
        >
          {drawer}
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0, // kriticke pro flex container s long content (URL, monospace) aby nepretekalo viewport
          minHeight: '100vh',
          overflowX: 'hidden',
          // AppBar spacer
          pt: { xs: 7, sm: 8 },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ flexGrow: 1, p: { xs: 2, sm: 3, md: 4 }, maxWidth: 1400, width: '100%', mx: 'auto' }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};
